// Service de compression et optimisation d'images pour performance maximale

export interface ImageCompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'webp' | 'avif' | 'jpeg' | 'png';
  maintainAspectRatio?: boolean;
}

/**
 * Compresse une image côté client avant l'upload
 */
export const compressImage = async (
  file: File,
  options: ImageCompressionOptions = {}
): Promise<Blob> => {
  const {
    maxWidth = 1920,
    maxHeight = 1080,
    quality = 0.55, // Ultra-compressé par défaut
    format = 'webp',
    maintainAspectRatio = true
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Créer un canvas pour la compression
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Calculer les dimensions optimales
        let width = img.width;
        let height = img.height;

        if (maintainAspectRatio) {
          const aspectRatio = width / height;
          
          if (width > maxWidth) {
            width = maxWidth;
            height = width / aspectRatio;
          }
          
          if (height > maxHeight) {
            height = maxHeight;
            width = height * aspectRatio;
          }
        } else {
          width = Math.min(width, maxWidth);
          height = Math.min(height, maxHeight);
        }

        // Définir les dimensions du canvas
        canvas.width = width;
        canvas.height = height;

        // Dessiner l'image redimensionnée
        ctx.drawImage(img, 0, 0, width, height);

        // Convertir en blob avec compression
        const mimeType = format === 'webp' ? 'image/webp' :
                        format === 'avif' ? 'image/avif' :
                        format === 'jpeg' ? 'image/jpeg' :
                        'image/png';

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          },
          mimeType,
          quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Génère des URLs optimisées pour Supabase Storage avec paramètres de transformation
 */
export const getOptimizedSupabaseUrl = (
  baseUrl: string,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    format?: 'webp' | 'avif' | 'origin';
    resize?: 'contain' | 'cover' | 'fill';
  } = {}
): string => {
  if (!baseUrl || !baseUrl.includes('supabase.co/storage')) {
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl);
    const { width, height, quality = 50, format = 'webp', resize = 'contain' } = options; // Ultra-compressé

    if (width) url.searchParams.set('width', width.toString());
    if (height) url.searchParams.set('height', height.toString());
    if (quality) url.searchParams.set('quality', quality.toString());
    if (format) url.searchParams.set('format', format);
    if (resize) url.searchParams.set('resize', resize);

    return url.toString();
  } catch (e) {
    console.error('Error generating optimized URL:', e);
    return baseUrl;
  }
};

/**
 * Génère un srcset responsive pour une image
 */
export const generateResponsiveSrcSet = (
  baseUrl: string,
  widths: number[] = [320, 640, 768, 1024, 1280, 1920],
  format: 'webp' | 'avif' = 'webp'
): string => {
  if (!baseUrl || !baseUrl.includes('supabase.co/storage')) {
    return '';
  }

  return widths
    .map(width => {
      const optimizedUrl = getOptimizedSupabaseUrl(baseUrl, {
        width,
        quality: 50, // Ultra-compressé
        format,
        resize: 'contain'
      });
      return `${optimizedUrl} ${width}w`;
    })
    .join(', ');
};

/**
 * Calcule la taille optimale d'une image selon son utilisation
 */
export const getOptimalImageSize = (
  usage: 'logo' | 'banner' | 'avatar' | 'thumbnail' | 'hero' | 'gallery'
): { width: number; height: number; quality: number } => {
  const sizes = {
    logo: { width: 120, height: 120, quality: 55 }, // Ultra-compressé
    avatar: { width: 80, height: 80, quality: 55 },
    thumbnail: { width: 400, height: 300, quality: 50 },
    banner: { width: 1200, height: 400, quality: 50 },
    hero: { width: 1920, height: 1080, quality: 50 },
    gallery: { width: 800, height: 600, quality: 50 }
  };

  return sizes[usage] || sizes.thumbnail;
};

/**
 * Détecte le support des formats modernes (WebP, AVIF)
 */
export const detectImageFormatSupport = async (): Promise<{
  webp: boolean;
  avif: boolean;
}> => {
  const checkWebP = () => {
    return new Promise<boolean>((resolve) => {
      const webpData = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=';
      const img = new Image();
      img.onload = () => resolve(img.width === 1);
      img.onerror = () => resolve(false);
      img.src = webpData;
    });
  };

  const checkAVIF = () => {
    return new Promise<boolean>((resolve) => {
      const avifData = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A=';
      const img = new Image();
      img.onload = () => resolve(img.width === 2);
      img.onerror = () => resolve(false);
      img.src = avifData;
    });
  };

  const [webp, avif] = await Promise.all([checkWebP(), checkAVIF()]);
  
  return { webp, avif };
};

/**
 * Retourne le meilleur format d'image supporté par le navigateur
 */
export const getBestImageFormat = async (): Promise<'avif' | 'webp' | 'jpeg'> => {
  const support = await detectImageFormatSupport();
  
  if (support.avif) return 'avif';
  if (support.webp) return 'webp';
  return 'jpeg';
};

// Cache pour éviter de re-détecter le support
let cachedFormatSupport: { webp: boolean; avif: boolean } | null = null;

export const getCachedFormatSupport = async () => {
  if (!cachedFormatSupport) {
    cachedFormatSupport = await detectImageFormatSupport();
  }
  return cachedFormatSupport;
};
